import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { IRequestRecord } from 'types/requests.interface'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { formatDateForDisplay } from 'utils/helpers'
import Avatar from '../../../assets/images/profileAvatar.png'
import {
  typeLabel,
  statusLabel,
  statusPill,
  typePill,
  durationLabel,
  balanceType,
  isApprover,
  requestDays,
  REQ_SHIFT,
  REQ_OVERTIME,
  ST_PENDING,
} from '../requestsData'
import {
  Scrim,
  Panel,
  Header,
  Identity,
  AvatarBox,
  IdentityText,
  Name,
  Meta,
  CloseBtn,
  Body,
  PillRow,
  PillGroup,
  Code,
  DateCards,
  DateCard,
  MiniLabel,
  MiniValue,
  MetaGrid,
  MetaItem,
  BalanceBox,
  BalanceCell,
  Reason,
  Callout,
  CalloutTitle,
  CalloutText,
  NoteLabel,
  NoteInput,
  DecisionBox,
  Actions,
} from './RequestDrawer.styled'

const submittedAgo = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'Today'
  return `${days}d ago`
}

export const RequestDrawer = ({
  req,
  scope,
  onClose,
  onApprove,
  onReject,
  onCancel,
}: {
  req: IRequestRecord
  scope: 'approvals' | 'mine'
  onClose: () => void
  onApprove: (note: string) => void
  onReject: (note: string) => void
  onCancel: () => void
}) => {
  const { currentUser, balances } = useSelector((state: RootState) => state.requests)
  const [note, setNote] = useState('')

  const bType = balanceType(req.type)
  const bal = bType ? balances.find((b) => b.employeeId === req.requester.id && b.type === bType) : null
  const remaining = bal ? bal.entitled - bal.used - bal.pending : 0
  const overBalance = !!bal && remaining < 0

  const pending = req.status === ST_PENDING
  const canApprove = isApprover(currentUser.role) && pending
  const canCancel = scope === 'mine' && pending && req.requester.id === currentUser.id

  return (
    <>
      <Scrim onClick={onClose} />
      <Panel>
        <Header>
          <Identity>
            <AvatarBox>
              <img src={req.requester.avatarUrl || Avatar} alt='' onError={({ currentTarget }) => (currentTarget.src = Avatar)} />
            </AvatarBox>
            <IdentityText>
              <Name>{req.requester.name}</Name>
              <Meta>
                {req.requester.jobTitle} · {req.requester.site}
              </Meta>
            </IdentityText>
          </Identity>
          <CloseBtn type='button' aria-label='Close' onClick={onClose}>
            <CloseIcon />
          </CloseBtn>
        </Header>

        <Body>
          <PillRow>
            <PillGroup>
              <StatusPill status={typePill(req.type)}>{typeLabel(req.type)}</StatusPill>
              <StatusPill status={statusPill(req.status)} dot>
                {statusLabel(req.status)}
              </StatusPill>
            </PillGroup>
            <Code>{req.code}</Code>
          </PillRow>

          <DateCards>
            <DateCard>
              <MiniLabel>From</MiniLabel>
              <MiniValue>{formatDateForDisplay(req.from)}</MiniValue>
            </DateCard>
            <DateCard>
              <MiniLabel>To</MiniLabel>
              <MiniValue>{formatDateForDisplay(req.to)}</MiniValue>
            </DateCard>
          </DateCards>

          <MetaGrid>
            <MetaItem>
              <MiniLabel>Coverage</MiniLabel>
              <MiniValue>{req.coverageName || '/'}</MiniValue>
            </MetaItem>
            <MetaItem>
              <MiniLabel>Submitted</MiniLabel>
              <MiniValue>{submittedAgo(req.submittedAt)}</MiniValue>
            </MetaItem>
            <MetaItem>
              <MiniLabel>Duration</MiniLabel>
              <MiniValue>{durationLabel(req)}</MiniValue>
            </MetaItem>
          </MetaGrid>

          {bal && (
            <BalanceBox $danger={overBalance}>
              <BalanceCell>
                <MiniLabel>Entitled</MiniLabel>
                <MiniValue>{bal.entitled}</MiniValue>
              </BalanceCell>
              <BalanceCell>
                <MiniLabel>Used</MiniLabel>
                <MiniValue>{bal.used}</MiniValue>
              </BalanceCell>
              <BalanceCell>
                <MiniLabel>This request</MiniLabel>
                <MiniValue>{requestDays(req)}</MiniValue>
              </BalanceCell>
              <BalanceCell>
                <MiniLabel>Remaining</MiniLabel>
                <MiniValue $danger={overBalance}>{remaining}</MiniValue>
              </BalanceCell>
            </BalanceBox>
          )}

          <MetaItem>
            <MiniLabel>Reason</MiniLabel>
            <Reason>{req.reason}</Reason>
          </MetaItem>

          {req.type === REQ_SHIFT && req.coverageConfirmed && req.coverageName && (
            <Callout>
              <CalloutTitle>Coverage confirmed</CalloutTitle>
              <CalloutText>{req.coverageName} has agreed to cover this shift.</CalloutText>
            </Callout>
          )}

          {req.type === REQ_OVERTIME && (
            <MetaItem>
              <MiniLabel>Extra hours</MiniLabel>
              <MiniValue>+{req.overtimeHours || 0}h</MiniValue>
            </MetaItem>
          )}

          {req.decision && (
            <DecisionBox>
              <MiniLabel>
                {req.status === 2 ? 'Approved' : 'Rejected'} by {req.decision.by} · {formatDateForDisplay(req.decision.at)}
              </MiniLabel>
              {req.decision.note && <Reason>{req.decision.note}</Reason>}
            </DecisionBox>
          )}

          {(canApprove || canCancel) && (
            <div>
              <NoteLabel>Manager note {canApprove ? '(required to reject)' : '(optional)'}</NoteLabel>
              <NoteInput
                placeholder='Add a note for the employee…'
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}
        </Body>

        {canApprove && (
          <Actions>
            <Button variant='danger' disabled={!note.trim()} onClick={() => onReject(note.trim())}>
              Reject
            </Button>
            <Button variant='primary' onClick={() => onApprove(note.trim())}>
              Approve
            </Button>
          </Actions>
        )}
        {!canApprove && canCancel && (
          <Actions>
            <Button variant='secondary' onClick={onCancel}>
              Cancel request
            </Button>
          </Actions>
        )}
      </Panel>
    </>
  )
}
